import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppComponent } from '../../app.component';
import { INSIGHTS_URL } from '../../app-urls';
import { MatDialog } from '@angular/material/dialog';
import { CURRENT_STATE_PROJECT_KEY, Linqset, ORGANISATION_MODEL_KEY } from '../../common/linqset-model';
import { EpmDecisionEvidence } from '../../common/epm-decisions';
import { FutureStateDialogComponent } from '../../analysis/future-state-dialog.component';
import { createLinqset } from '../../common/model-processing';
import { createInsightsFilter, DEFAULT_CURRENCY, getCurrency, ModelDetails, Organisation } from '../../common/organisation';
import { AnalysisScope, ScopeService, wholeOrganisation } from '../../analysis/scope';
import { LEVER_LABELS, previewRecommendations, Recommendation, RecommendationLever, RecommendationSource, SOURCE_LABELS } from '../../analysis/recommendations';
import { Headline, ModelRow, OverviewService } from '../../analysis/overview.service';
import { LENSES, LensKey } from '../../lenses/lens';
import { FormattingService } from '../../linq-utils/formatting/formatting.service';
import { FUNCTION, FunctionService } from '../../model-services/function.service';
import { HelpService } from '../../help/help-services';
import { analysisTour } from '../../analysis/analysis-tour';
import { OrganisationService } from '../../organisation/organisation.service';
import { SCREEN } from '../layouts-enums';
import { USER_ROLE } from '../../common/user';

export type { Headline, ModelRow } from '../../analysis/overview.service';

// The Overview of the analysis: the headline figures for what is looked at, what to do first (one list, from every
// report), how each model is doing, and the data to fix. Every figure links to the view that explains it. Narrowing to
// a model from the table keeps the same views and recommendations, just for that model
@Component({
    selector: 'app-overview-layout',
    templateUrl: './overview-layout.html',
    styleUrls: ['./overview-layout.css'],
    standalone: false,
})
export class OverviewLayoutComponent implements OnInit, OnDestroy {
    readonly screen = SCREEN.ANALYSIS;
    readonly leverLabels = LEVER_LABELS;
    readonly sourceLabels = SOURCE_LABELS;
    readonly levers: RecommendationLever[] = ['capacity', 'automate', 'integrate', 'digitise', 'eliminate', 'protect', 'value'];

    organisationKey = '';
    organisation?: Organisation;
    linqset?: Linqset;
    scope: AnalysisScope = wholeOrganisation();
    loading = true;
    error = '';

    scopeDescription = '';
    workspaceName = '';
    models: ModelDetails[] = [];
    headlines: Headline[] = [];
    recommendations: Recommendation[] = [];
    dataGaps: Recommendation[] = [];
    epmDataGaps: string[] = [];
    rows: ModelRow[] = [];
    allRecommendations = false;
    allRows = false;
    leverFilter?: RecommendationLever;
    selected = new Set<string>();           // the recommendations ticked for a future state, by key
    private evidence?: EpmDecisionEvidence; // the EPM scorecard's, which a future state is generated from

    private subs: Subscription[] = [];
    private request = 0;
    compared = false;           // the figures are shown beside the current state's

    constructor(private route: ActivatedRoute, private router: Router, private organisationService: OrganisationService, private overviewService: OverviewService,
        private functionService: FunctionService, private formatter: FormattingService, private scopeService: ScopeService, private dialog: MatDialog,
        private helpService: HelpService) {}

    ngOnInit() {
        this.subs.push(this.route.params.subscribe(p => {
            this.organisationKey = p['organisation_key'];
            const q = this.route.snapshot.queryParams;
            // a link from a model or the old Insights page says what to look at
            const fromLink = q['workspaceKey'] || q['modelKey'] ? { ...wholeOrganisation(q['workspaceKey']), modelKeys: q['modelKey'] && q['modelKey'] !== ORGANISATION_MODEL_KEY ? [q['modelKey']] : [] } : undefined;
            this.scopeService.open(this.organisationKey, fromLink);
            if (fromLink) void this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
        }));
        this.subs.push(this.scopeService.scope$.subscribe(scope => {
            this.scope = scope;
            void this.load();
        }));
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
        this.helpService.endTour();
    }

    private async load() {
        const request = ++this.request;
        this.loading = true;
        this.error = '';
        try {
            const scope = this.scope;
            const organisation = await this.organisationService.checkOrganisationDataForRoute(this.organisationKey);
            if (request !== this.request) return;
            // a project is set beside the current state, so each figure shows how it changes
            const comparing = scope.workspaceKey !== CURRENT_STATE_PROJECT_KEY;
            const [data, currentState] = await Promise.all([
                this.overviewService.load(organisation, scope),
                comparing ? this.overviewService.load(organisation, { ...scope, workspaceKey: CURRENT_STATE_PROJECT_KEY }).catch(err => {
                    console.error('Unable to load the current state to compare with', err);
                    return undefined;
                }) : undefined,
            ]);
            if (currentState) this.overviewService.compare(data, currentState, organisation);
            if (request !== this.request) return;
            this.organisation = organisation;
            this.compared = !!currentState;
            this.linqset = createLinqset(organisation, createInsightsFilter(scope.workspaceKey));
            this.models = data.models;
            this.workspaceName = data.workspaceName;
            this.scopeDescription = data.scopeDescription;
            this.headlines = data.headlines;
            this.recommendations = data.recommendations;
            this.dataGaps = data.dataGaps;
            this.epmDataGaps = data.epmDataGaps;
            this.rows = data.rows;
            this.evidence = data.epm.evidence;
            // ticks for recommendations no longer listed go
            const listed = new Set(this.recommendations.map(r => r.key));
            this.selected = new Set([...this.selected].filter(k => listed.has(k)));
        } catch (err) {
            if (request !== this.request) return;
            console.error('Unable to load the overview', err);
            this.error = "The overview couldn't be loaded. Please try again.";
        } finally {
            if (request === this.request) this.loading = false;
        }
    }

    // to start with, at most three to act on now, so the largest of the rest show too
    get shownRecommendations(): Recommendation[] {
        const list = this.leverFilter ? this.recommendations.filter(r => r.lever === this.leverFilter) : this.recommendations;
        return this.allRecommendations ? list : previewRecommendations(list, 8, 3);
    }

    // those to act on now that the short list leaves out
    get hiddenUrgent() {
        if (this.allRecommendations) return 0;
        const shown = this.shownRecommendations.filter(r => r.urgent).length;
        const list = this.leverFilter ? this.recommendations.filter(r => r.lever === this.leverFilter) : this.recommendations;
        return list.filter(r => r.urgent).length - shown;
    }

    get filteredCount() {
        return this.leverFilter ? this.recommendations.filter(r => r.lever === this.leverFilter).length : this.recommendations.length;
    }

    get shownRows(): ModelRow[] {
        return this.allRows ? this.rows : this.rows.slice(0, 15);
    }

    leversPresent(): RecommendationLever[] {
        return this.levers.filter(l => this.recommendations.some(r => r.lever === l));
    }

    toggleLever(lever: RecommendationLever) {
        this.leverFilter = this.leverFilter === lever ? undefined : lever;
    }

    // a future state builds on what's looked at: current state, or a copy of the project chosen. It's a new project, so
    // those who can only read the organisation can't create one
    get canCreateFutureState() {
        return !!this.evidence && !!this.organisation && this.organisation.role !== USER_ROLE.READ && (this.scope.workspaceKey === CURRENT_STATE_PROJECT_KEY || !!this.baseProject);
    }

    // the changes can be made directly to a project that's looked at, by those who can edit it. Current state is only
    // changed by merging a project into it, so a future state of it is always a new project
    get canChangeWorkspace() {
        if (!this.organisation || this.organisation.role === USER_ROLE.READ) return false;
        return !!this.baseProject && this.baseProject.role !== USER_ROLE.READ;
    }

    // where the changes can be made: a new project, or the workspace looked at
    get futureStateTooltip() {
        if (this.organisation?.role === USER_ROLE.READ) return "You can only view this organisation, so you can't create a future state"
        return `Make the changes in a new project${this.canChangeWorkspace ? ', or in the project you\'re looking at' : ''}`;
    }

    private get baseProject() {
        return this.scope.workspaceKey === CURRENT_STATE_PROJECT_KEY ? undefined : this.organisation?.projects?.find(p => p._key === this.scope.workspaceKey);
    }

    toggleSelected(r: Recommendation, selected: boolean) {
        if (selected) this.selected.add(r.key);
        else this.selected.delete(r.key);
    }

    // ticks every recommendation of the kind filtered to, including those the short list leaves out
    selectAll() {
        const list = this.leverFilter ? this.recommendations.filter(r => r.lever === this.leverFilter) : this.recommendations;
        list.forEach(r => this.selected.add(r.key));
    }

    get allSelected() {
        const list = this.leverFilter ? this.recommendations.filter(r => r.lever === this.leverFilter) : this.recommendations;
        return list.every(r => this.selected.has(r.key));
    }

    // AI makes the ticked recommendations, and anything the user describes, in a new project, which the overview can
    // then show, or in the workspace looked at, after which its figures are worked out again
    openFutureState() {
        if (!this.canCreateFutureState) return;
        const recommendations = this.recommendations.filter(r => this.selected.has(r.key));
        FutureStateDialogComponent.open(this.dialog, { organisation: this.organisation!, evidence: this.evidence!, recommendations, base: this.baseProject, canChangeWorkspace: this.canChangeWorkspace })
            .afterClosed().subscribe(result => {
                // the workspace looked at was changed, so its figures and recommendations are worked out again
                if (result?.changed) {
                    this.selected.clear();
                    void this.load();
                    return;
                }
                if (!result?.project) return;
                this.selected.clear();
                if (result.open) this.scopeService.set({ ...this.scope, workspaceKey: result.project._key });
            });
    }

    // the view that shows a recommendation, with the node it is about
    openRecommendation(r: Recommendation) {
        const lens = LENSES.find(l => l.key === r.lens);
        if (!lens || !this.organisation) return;
        void this.router.navigate([lens.url(this.organisation._key)], r.focus ? { queryParams: { focus: r.focus } } : {});
    }

    openLens(key: LensKey) {
        const lens = LENSES.find(l => l.key === key);
        if (lens && this.organisation) void this.router.navigateByUrl(lens.url(this.organisation._key));
    }

    // narrows what every view looks at to the model
    openModel(row: ModelRow) {
        this.scopeService.narrowTo([row.key]);
    }

    // the Insights page still holds the node tables, narrative and AI summary for what is looked at
    get classicInsightsUrl() {
        if (!this.organisation) return '';
        const params = [`workspaceKey=${this.scope.workspaceKey}`];
        if (this.models.length === 1) params.push(`modelKey=${this.models[0]._key}`);
        return `${INSIGHTS_URL(this.organisation._key)}?${params.join('&')}`;
    }

    openClassicInsights() {
        if (this.classicInsightsUrl) void this.router.navigateByUrl(this.classicInsightsUrl);
    }

    sourceList(sources: RecommendationSource[]) {
        return sources.map(s => SOURCE_LABELS[s]).join(', ');
    }

    hours(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    money(value: number) {
        return this.formatter.formatAbbreviatedNumber(value, getCurrency(this.organisation?.currencyCode || DEFAULT_CURRENCY));
    }

    percent(value: number | null) {
        return value === null ? '–' : `${Math.round(value * 100)}%`;
    }

    async handleFunctionEvent(event: { function: FUNCTION; value?: any }) {
        if (event.function === FUNCTION.SCREEN_TOUR) {
            this.helpService.showWhatsThisOverlay(analysisTour('overview'));
            return;
        }
        try {
            await this.functionService.executeFunction(event.function, event.value, undefined, undefined, undefined);
        } catch (err) {
            console.error(err);
        }
    }

    isHelpOn() {
        return AppComponent.help;
    }
}
